// Permissions module — mirrors product-specification.md §3 verbatim.
//
// Per frontend-design §1 Authorization rendering: the server is the
// authoritative permission check. This module is **advisory** — it
// chooses what to render (hidden / disabled / blocking-modal) per the
// G-09 three-pattern denial model. A request that bypasses these checks
// is rejected by the server.

import type { Agreement, AgreementStatus, Principal } from '@/types/domain';

export type DenialReason =
  | 'role'
  | 'ownership'
  | 'completed_state'
  | 'archived_state';

export interface AffordanceState {
  // Render rule per G-09:
  //   'visible'  — render normally
  //   'disabled' — render disabled with tooltip (ownership-based)
  //   'hidden'   — do not render (role-based or state-based per spec)
  rendering: 'visible' | 'disabled' | 'hidden';
  reason?: DenialReason;
  tooltip?: string;
}

const VISIBLE: AffordanceState = { rendering: 'visible' };
const HIDDEN_ROLE: AffordanceState = { rendering: 'hidden', reason: 'role' };
const HIDDEN_STATE_COMPLETED: AffordanceState = {
  rendering: 'hidden',
  reason: 'completed_state',
};
const HIDDEN_STATE_ARCHIVED: AffordanceState = {
  rendering: 'hidden',
  reason: 'archived_state',
};

function disabledOwnership(action: string): AffordanceState {
  return {
    rendering: 'disabled',
    reason: 'ownership',
    tooltip: `You can only ${action} agreements you own.`,
  };
}

export function isAdmin(p: Principal | null): boolean {
  return p?.role === 'admin';
}
export function isPL(p: Principal | null): boolean {
  return p?.role === 'practice_lead';
}
export function isMember(p: Principal | null): boolean {
  return p?.role === 'member';
}

export function isOwnerOf(p: Principal | null, agreement: Agreement): boolean {
  return p ? p.person_id === agreement.owner_person_id : false;
}

// Spec §3 / G-10: top-nav inventory per role.
export interface NavLink {
  to: string;
  label: string;
}
export function navLinks(p: Principal | null): NavLink[] {
  if (!p) return [];
  if (p.role === 'admin') {
    return [
      { to: '/agreements', label: 'Agreements' },
      { to: '/me/assignments', label: 'My Assignments' },
      { to: '/dashboard', label: 'Dashboard' },
      { to: '/admin/people', label: 'People' },
      { to: '/admin/companies', label: 'Companies' },
    ];
  }
  if (p.role === 'practice_lead') {
    return [
      { to: '/agreements', label: 'Agreements' },
      { to: '/me/assignments', label: 'My Assignments' },
      { to: '/dashboard', label: 'Dashboard' },
    ];
  }
  // Member — G-10 Member primary nav = My Assignments only.
  return [{ to: '/me/assignments', label: 'My Assignments' }];
}

export function homeRouteFor(p: Principal | null): string {
  if (!p) return '/me/assignments';
  if (p.role === 'member') return '/me/assignments';
  return '/agreements';
}

export function canViewDashboard(p: Principal | null): boolean {
  return isAdmin(p) || isPL(p);
}

export function canViewAdminSurfaces(p: Principal | null): boolean {
  return isAdmin(p);
}

export function canCreateAgreement(p: Principal | null): AffordanceState {
  if (!p) return HIDDEN_ROLE;
  if (p.role === 'member') return HIDDEN_ROLE;
  return VISIBLE;
}

// Edit-on-Completed (spec §3): on Completed agreements, only Admin can
// edit Assignment.End Date. Edits to the Agreement itself are forbidden
// for all roles. On Archived, all edits forbidden.
export function canEditAgreement(
  p: Principal | null,
  a: Agreement,
): AffordanceState {
  if (!p) return HIDDEN_ROLE;
  if (a.status === 'archived') return HIDDEN_STATE_ARCHIVED;
  if (a.status === 'completed') return HIDDEN_STATE_COMPLETED;
  if (p.role === 'member') return HIDDEN_ROLE;
  if (p.role === 'admin') return VISIBLE;
  // PL — own only.
  if (isOwnerOf(p, a)) return VISIBLE;
  return disabledOwnership('edit');
}

// Activate: Admin or PL-on-own; only valid on Draft. The precondition
// check (BR-05/06, ≥1 assignment) is computed in the page parent and
// gates the *enabled* state separately.
export function canActivateAgreement(
  p: Principal | null,
  a: Agreement,
): AffordanceState {
  if (!p) return HIDDEN_ROLE;
  if (a.status !== 'draft') return { rendering: 'hidden' };
  if (p.role === 'member') return HIDDEN_ROLE;
  if (p.role === 'admin') return VISIBLE;
  if (isOwnerOf(p, a)) return VISIBLE;
  return disabledOwnership('activate');
}

export function canCompleteAgreement(
  p: Principal | null,
  a: Agreement,
): AffordanceState {
  if (!p) return HIDDEN_ROLE;
  if (a.status !== 'active') return { rendering: 'hidden' };
  if (p.role === 'member') return HIDDEN_ROLE;
  if (p.role === 'admin') return VISIBLE;
  if (isOwnerOf(p, a)) return VISIBLE;
  return disabledOwnership('complete');
}

export function canRevertAgreement(
  p: Principal | null,
  a: Agreement,
): AffordanceState {
  if (!p) return HIDDEN_ROLE;
  if (a.status !== 'completed') return { rendering: 'hidden' };
  // Admin only per spec §3 / §2 state machine.
  if (p.role !== 'admin') return HIDDEN_ROLE;
  return VISIBLE;
}

export function canArchiveAgreement(
  p: Principal | null,
  a: Agreement,
): AffordanceState {
  if (!p) return HIDDEN_ROLE;
  if (a.status !== 'completed') return { rendering: 'hidden' };
  if (p.role !== 'admin') return HIDDEN_ROLE;
  return VISIBLE;
}

// Amend: any status (per F-9), Admin or PL-on-own.
export function canAmendAgreement(
  p: Principal | null,
  a: Agreement,
): AffordanceState {
  if (!p) return HIDDEN_ROLE;
  if (p.role === 'member') return HIDDEN_ROLE;
  if (p.role === 'admin') return VISIBLE;
  if (isOwnerOf(p, a)) return VISIBLE;
  return disabledOwnership('amend');
}

// Add / Edit / Delete Assignment on this agreement (F-2).
// Hidden on Completed/Archived for non-Admin; Admin has the
// edit-end-date-only mode on Completed (handled by the row, not here).
export function canEditAssignmentsOn(
  p: Principal | null,
  a: Agreement,
): AffordanceState {
  if (!p) return HIDDEN_ROLE;
  if (a.status === 'archived') return HIDDEN_STATE_ARCHIVED;
  if (a.status === 'completed') return HIDDEN_STATE_COMPLETED;
  if (p.role === 'member') return HIDDEN_ROLE;
  if (p.role === 'admin') return VISIBLE;
  if (isOwnerOf(p, a)) return VISIBLE;
  return disabledOwnership('add or edit assignments on');
}

// Edit Assignment.end_date on a Completed agreement — Admin only.
export function canEditAssignmentEndDateOnCompleted(
  p: Principal | null,
  a: Agreement,
): AffordanceState {
  if (!p) return HIDDEN_ROLE;
  if (a.status !== 'completed') return { rendering: 'hidden' };
  if (p.role !== 'admin') return HIDDEN_ROLE;
  return VISIBLE;
}

// Edit Credits / Schedule reuses the same gating as assignments.
export const canEditCreditsOn = canEditAssignmentsOn;
export const canEditScheduleOn = canEditAssignmentsOn;

// Tooltip helper for state-based hidden→tooltip conversion when a
// component prefers to show a disabled-with-tooltip rather than fully
// hidden (per per-component contracts in frontend-design §3).
export function stateTooltip(s: AgreementStatus): string {
  if (s === 'completed') return "Completed agreements can't be edited.";
  if (s === 'archived') return 'Archived agreements are read-only.';
  return '';
}
